import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";

export const FIELD_TYPES = {
  TEXT: "text",
  DROPDOWN: "dropdown",
  MULTISELECT: "multiselect",
  CREATABLE_MULTISELECT: "creatableMultiselect",
} as const;

export type FieldType = (typeof FIELD_TYPES)[keyof typeof FIELD_TYPES];

export const PROFILE_FIELD_KEYS = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  COUNTRY: "country",
  EMAIL: "email",
  COMPANY: "company",
  TITLE: "title",
  ROLE: "role",
  FUNCTION: "function",
  EXPERIENCE: "experience",
  EXPERTISE: "expertise",
  LANGUAGE: "language",
  ADDITIONAL_LANGUAGES: "additionalLanguages",
  TOOLING: "tooling",
  AI_MODELS: "aiModels",
  AI_ASSISTANTS: "aiAssistants",
  AI_FRAMEWORKS: "aiFrameworks",
  AI_PLATFORMS: "aiPlatforms",
  DOES_NOT_USE_AI: "doesNotUseAi",
} as const;

export type ProfileFieldKey = (typeof PROFILE_FIELD_KEYS)[keyof typeof PROFILE_FIELD_KEYS];

const AI_CATEGORY_FORM_KEYS: Record<string, ProfileFieldKey> = {
  models: PROFILE_FIELD_KEYS.AI_MODELS,
  assistants: PROFILE_FIELD_KEYS.AI_ASSISTANTS,
  frameworks: PROFILE_FIELD_KEYS.AI_FRAMEWORKS,
  platforms: PROFILE_FIELD_KEYS.AI_PLATFORMS,
};

export interface FieldDefinition {
  label: string;
  key: ProfileFieldKey;
  type: FieldType;
  options?: { label: string; value: string }[];
  hasOtherInput?: boolean;
}

// Rendered as their own section (with the "I don't use AI tools" opt-out)
// rather than through FIELD_GROUPS
export const AI_TOOL_FIELDS = DeveloperProfileConstants.OPTIONS.aiToolCategories.map(category => ({
  label: category.label,
  key: AI_CATEGORY_FORM_KEYS[category.key] ?? (category.key as ProfileFieldKey),
  categoryKey: category.key,
  type: FIELD_TYPES.CREATABLE_MULTISELECT,
  options: category.options,
}));

// Temporarily disabling email editing for all users
// TODO: Add user initiated email change flow - shrpa:3089 as reference
export const ALWAYS_LOCKED_FIELDS: ProfileFieldKey[] = [PROFILE_FIELD_KEYS.EMAIL];

export const SSO_LOCKED_FIELDS: ProfileFieldKey[] = [
  PROFILE_FIELD_KEYS.FIRST_NAME,
  PROFILE_FIELD_KEYS.LAST_NAME,
  PROFILE_FIELD_KEYS.COUNTRY,
  PROFILE_FIELD_KEYS.EMAIL,
  PROFILE_FIELD_KEYS.COMPANY,
  PROFILE_FIELD_KEYS.TITLE,
  PROFILE_FIELD_KEYS.ROLE,
];

export const FIELD_GROUPS: { title?: string; fields: FieldDefinition[] }[] = [
  {
    fields: [
      { label: "First Name", key: PROFILE_FIELD_KEYS.FIRST_NAME, type: FIELD_TYPES.TEXT },
      { label: "Last Name", key: PROFILE_FIELD_KEYS.LAST_NAME, type: FIELD_TYPES.TEXT },
      {
        label: "Country",
        key: PROFILE_FIELD_KEYS.COUNTRY,
        type: FIELD_TYPES.DROPDOWN,
        options: [],
      },
      { label: "Email Address", key: PROFILE_FIELD_KEYS.EMAIL, type: FIELD_TYPES.TEXT },
    ],
  },
  {
    fields: [
      { label: "Company", key: PROFILE_FIELD_KEYS.COMPANY, type: FIELD_TYPES.TEXT },
      { label: "Title", key: PROFILE_FIELD_KEYS.TITLE, type: FIELD_TYPES.TEXT },
      { label: "Role", key: PROFILE_FIELD_KEYS.ROLE, type: FIELD_TYPES.TEXT },
      {
        label: "Job Function",
        key: PROFILE_FIELD_KEYS.FUNCTION,
        type: FIELD_TYPES.DROPDOWN,
        options: DeveloperProfileConstants.OPTIONS.jobRoles,
        hasOtherInput: true,
      },
    ],
  },
  {
    title: "Technical Expertise",
    fields: [
      {
        label: "Years of Experience",
        key: PROFILE_FIELD_KEYS.EXPERIENCE,
        type: FIELD_TYPES.DROPDOWN,
        options: DeveloperProfileConstants.OPTIONS.experienceLevels,
      },
      {
        label: "Code Involvement",
        key: PROFILE_FIELD_KEYS.EXPERTISE,
        type: FIELD_TYPES.DROPDOWN,
        options: DeveloperProfileConstants.OPTIONS.technicalExpertise,
      },
      {
        label: "Programming Language",
        key: PROFILE_FIELD_KEYS.LANGUAGE,
        type: FIELD_TYPES.DROPDOWN,
        options: DeveloperProfileConstants.OPTIONS.programmingLanguages,
        hasOtherInput: true,
      },
      {
        label: "Additional Programming Languages",
        key: PROFILE_FIELD_KEYS.ADDITIONAL_LANGUAGES,
        type: FIELD_TYPES.MULTISELECT,
        options: DeveloperProfileConstants.OPTIONS.additionalLanguages,
      },
      {
        label: "Architecture & Tools/Topics",
        key: PROFILE_FIELD_KEYS.TOOLING,
        type: FIELD_TYPES.MULTISELECT,
        options: DeveloperProfileConstants.OPTIONS.architectureTools,
        hasOtherInput: true,
      },
    ],
  },
];
